/** biome-ignore-all lint/complexity/noStaticOnlyClass: AuthService is a utility class with static methods only */
import { EmailStatus } from '@customTypes/auth.type'
import {
  signUp,
  signIn,
  resetPassword,
  confirmResetPassword,
  updateUserAttributes,
  confirmSignUp,
  autoSignIn,
  resendSignUpCode,
  updatePassword,
  deleteUser,
} from 'aws-amplify/auth'
import axios from 'axios'

const apiBaseUrl = import.meta.env.VITE_API_BASE_URL
/**
 * AuthService provides static methods for user authentication and profile management using AWS Amplify Auth.
 */
export class AuthService {
  /**
   * Registers a new user with email, password, first name, and last name.
   * @param params - User registration details
   * @returns The signUp result: { isSignUpComplete, userId (optional), nextStep }
   */
  static async signUp({
    email,
    password,
    name,
    positon,
    company,
  }: {
    email: string
    password: string
    name: string
    positon?: string
    company?: string
  }): Promise<{ isSignUpComplete: boolean; userId?: string; nextStep: any }> {
    try {
      const result = await signUp({
        username: email,
        password,
        options: {
          userAttributes: {
            email,
            name,
            'custom:Position': positon,
            'custom:Company': company,
          },
          autoSignIn: true,
        },
      })
      return result
    } catch (error: any) {
      // Log the exact error details for debugging
      console.error('SignUp Error Details:', {
        name: error?.name,
        message: error?.message,
        code: error?.code,
        statusCode: error?.statusCode,
        recoverySuggestion: error?.recoverySuggestion,
        underlyingError: error?.underlyingError,
        fullError: error,
      })
      throw error
    }
  }

  /**
   * Confirms user registration with the verification code sent to email.
   * @param params - Confirmation details
   * @returns Promise<void>
   */
  static async confirmSignUp({
    email,
    code,
  }: {
    email: string
    code: string
  }): Promise<void> {
    try {
      await confirmSignUp({
        username: email,
        confirmationCode: code,
      })
    } catch (error) {
      throw error
    }
  }

  /**
   * Resends the confirmation code to the user's email for sign-up verification.
   * @param email - User's email address
   * @returns Promise<void>
   */
  static async resendSignUpCode(email: string): Promise<void> {
    try {
      await resendSignUpCode({
        username: email,
      })
    } catch (error) {
      throw error
    }
  }

  /**
   * Automatically signs in a user after successful sign-up confirmation.
   * This should be called after confirmSignUp when autoSignIn was enabled during signUp.
   * @returns Promise with sign-in result containing user information
   */
  static async autoSignIn(): Promise<{ user: any }> {
    try {
      const signInOutput = await autoSignIn()
      return { user: signInOutput }
    } catch (error) {
      throw error
    }
  }

  /**
   * Signs in a user with email and password.
   * @param params - Login credentials
   */
  static async signIn({
    email,
    password,
  }: {
    email: string
    password: string
  }): Promise<{ user: any }> {
    try {
      const user = await signIn({ username: email, password })
      return { user }
    } catch (error) {
      throw error
    }
  }

  /**
   * Initiates the password reset process for a given email.
   * @param email - User's email address
   */
  static async initiateResetPassword(email: string): Promise<void> {
    try {
      await resetPassword({ username: email })
    } catch (error) {
      throw error
    }
  }

  /**
   * Confirms the new password using the code sent to the user's email.
   * @param params - Confirmation details
   */
  static async confirmResetPassword({
    email,
    code,
    newPassword,
  }: {
    email: string
    code: string
    newPassword: string
  }): Promise<void> {
    try {
      await confirmResetPassword({
        username: email,
        confirmationCode: code,
        newPassword,
      })
    } catch (error) {
      throw error
    }
  }

  /**
   * Updates the user's password.
   * @param params - Password details
   */
  static async updatePassword({
    currentPassword,
    newPassword,
  }: {
    currentPassword: string
    newPassword: string
  }): Promise<void> {
    try {
      await updatePassword({
        oldPassword: currentPassword,
        newPassword,
      })
    } catch (error) {
      throw error
    }
  }

  /**
   * Updates the user's profile with company and position after signup.
   * Updates Cognito custom attributes: custom:Company and custom:Position.
   * @param params - Profile details
   */
  static async updateProfile({
    company,
    position,
  }: {
    company: string
    position: string
  }): Promise<void> {
    try {
      await updateUserAttributes({
        userAttributes: {
          'custom:Company': company,
          'custom:Position': position,
        },
      })
    } catch (error) {
      throw error
    }
  }

  /**
   * Checks whether an email is already registered and verification status.
   *
   * @param email - Email address to check
   * @returns { exists: boolean, verified?: boolean } - Whether email is registered and verified
   */
  static async checkEmailExists(
    email: string,
    captchaToken: string
  ): Promise<{ status: EmailStatus }> {
    // 🚀 PRODUCTION: Replace above with Lambda backend call:
    const response = await axios.post(`${apiBaseUrl}/auth/discover`, {
      email,
      captchaToken,
    })
    return response.data
  }

  /**
   * Deletes a user account.
   * @returns Promise<void>
   */
  static async deleteUserCognito(): Promise<void> {
    try {
      await deleteUser()
    } catch (error) {
      throw error
    }
  }
}
